import isHuskyInstalled from "@buurman-feature-detectors/husky";
import {
    git,
    json,
    npm,
    pkg,
    Task,
    TaskCategory,
    TaskOptions,
    TaskOptionType,
} from "@buurman/sdk";
import { pathExists } from "fs-extra";
import { Huskyrc } from "./models";
import { huskyrcJson } from "./transformers";
import { validateHooks } from "./validators";

export interface HuskyTaskOptions extends TaskOptions {
    hooks: Huskyrc["hooks"];
}

const devDependencies = ["husky"];

export interface HuskyTask extends Task<HuskyTaskOptions> {
    hasHook(hook: string, command?: string): Promise<boolean>;
    addHook(hook: string, command: string): Promise<void>;
}

const task: HuskyTask = {
    name: "husky",
    npmPackage: require("../package.json").name,
    description: "Git hooks made easy",
    category: TaskCategory.VCS,
    tags: ["husky", "git", "git-hooks", "git-hook"],
    options: {
        hooks: {
            name: "hooks",
            required: false,
            type: TaskOptionType.OBJECT,
            defaultValue: {},
            validate: validateHooks,
        },
    },
    isInstalled: isHuskyInstalled,
    isSupported: async () =>
        (await pkg().exists()) && (await pathExists(".git")),
    addHook: (hook, command) =>
        json(".huskyrc.json")
            .setProperty(["hooks", hook], command)
            .save(),
    hasHook: (hook, command) =>
        json(".huskyrc.json").hasProperty(hook, command),
    apply: async ({ hooks }) => {
        const startedWithACleanWorkingCopy = await git().hasCleanWorkingCopy();

        await npm()
            .install(devDependencies, ["-D"])
            .execute();

        await json<Huskyrc>(".huskyrc.json")
            .transform(huskyrcJson(hooks))
            .save();

        const endedWithACleanWorkingCopy = await git().hasCleanWorkingCopy();

        if (startedWithACleanWorkingCopy && !endedWithACleanWorkingCopy) {
            await git()
                .add(".")
                .commit("chore: add husky")
                .execute();
        }
    },
};

export * from "./models";
export * from "./constants";
export * from "./transformers";
export * from "./validators";

export default task;
